/* Borderline winter 26/27 · "Finish the kit" category tiles (H3: sections/bl-kit-tiles.liquid, assets/bl-kit-tiles.css).
   <bl-kit-tiles data-sport="ski" data-follow="1">: shows the tiles of one sport (every "all" tile plus the sport's own)
   and, with data-follow, takes the sport from the wall shelf's `shelf:show` events (bl-shelf.js; a mixed tab says
   nothing and the last sport stays). On phones the odd last tile is marked .is-wide; on tablets (three columns) the
   last tile is marked .is-span-2 / .is-span-3 to fill its row. The drawings move as in the
   window (bl-cat.css): once on entering the viewport (50% in view, 60 ms apart) and on mouse hover / keyboard focus;
   nothing under prefers-reduced-motion. */
import { Component } from '@theme/component';

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [items] - The tiles' list items (data-sport: all / ski / snowboard).
 * @property {HTMLElement[]} [tiles] - The tiles themselves (the drawings' hosts).
 *
 * @extends Component<Refs>
 */
class BlKitTiles extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<ReturnType<typeof setTimeout>>} */
  #timers = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const open = document.querySelector('bl-shelf-component [role="tab"][aria-selected="true"]');
    const wall = open instanceof HTMLElement && this.dataset.follow ? open.dataset.sport || '' : '';
    this.#apply(wall === 'ski' || wall === 'snowboard' ? wall : this.dataset.sport || 'ski');
    if (this.dataset.follow) document.addEventListener('shelf:show', this.#onShow, { signal });

    for (const tile of this.refs.tiles || []) {
      tile.querySelectorAll('.hatch path').forEach((path, index) => {
        if (path instanceof SVGElement) path.style.animationDelay = `${Math.min(index * 0.02, 0.4)}s`;
      });
      tile.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'mouse') this.#play(tile, 'hover');
        },
        { signal }
      );
      tile.addEventListener('focus', () => this.#play(tile, 'hover'), { signal });
    }

    if (!('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;
          this.#observer?.unobserve(entry.target);
          entry.target.dataset.played = '1';
          this.#play(entry.target, 'enter', (Number(entry.target.dataset.order) || 0) * 60);
        }
      },
      { threshold: 0.5 }
    );
    for (const tile of this.refs.tiles || []) {
      if (!tile.dataset.played) this.#observer.observe(tile);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    for (const tile of this.refs.tiles || []) {
      tile.classList.remove('is-enter', 'is-hover');
      delete tile.dataset.busy;
    }
  }

  /**
   * The wall shelf opened a tab: its sport, when it has one.
   * @param {Event} event
   */
  #onShow = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const sport = event.detail?.sport;
    if (sport === 'ski' || sport === 'snowboard') this.#apply(sport);
  };

  /**
   * Show the tiles of one sport; the last tile takes the cells its row leaves empty: .is-wide on phones (two columns,
   * odd count), .is-span-2 / .is-span-3 on tablets (three columns, bl-kit-tiles.css).
   * @param {string} sport
   */
  #apply(sport) {
    this.dataset.sport = sport;
    /** @type {HTMLElement[]} */
    const shown = [];
    for (const item of this.refs.items || []) {
      const own = item.dataset.sport || 'all';
      const on = own === 'all' || own === sport;
      item.hidden = !on;
      item.classList.remove('is-wide', 'is-span-2', 'is-span-3');
      if (on) shown.push(item);
    }
    const last = shown[shown.length - 1];
    if (!last) return;
    const wide = shown.length % 2 === 1;
    if (wide) last.classList.add('is-wide');
    const rest = shown.length % 3;
    if (rest) last.classList.add(rest === 1 ? 'is-span-3' : 'is-span-2');

    // the pictures' sizes follow the span: the Liquid (sections/bl-kit-tiles.liquid) writes one cell's, but the last
    // tile is a whole row on a tablet (3:1) or a phone (2:1) and got a third / half-width picture (R2_screens 10/21)
    for (const item of shown) {
      const img = item.querySelector('img[srcset]');
      if (!(img instanceof HTMLImageElement)) continue;
      const tablet = item === last && rest ? (rest === 1 ? '100vw' : '67vw') : '33vw';
      const phone = item === last && wide ? '100vw' : '50vw';
      const sizes = `(min-width: 990px) calc((100vw - 40px) / 5), (min-width: 768px) ${tablet}, ${phone}`;
      if (img.sizes !== sizes) img.sizes = sizes;
    }
  }

  /**
   * Adds is-enter / is-hover for the length of the move, then takes it off; one move at a time per tile.
   * @param {HTMLElement} tile
   * @param {'enter' | 'hover'} mode
   * @param {number} [delay] - ms before the move starts
   */
  #play(tile, mode, delay = 0) {
    if (reducedMotion.matches || tile.dataset.busy) return;
    tile.dataset.busy = '1';
    const later = (/** @type {() => void} */ fn, /** @type {number} */ ms) => {
      const timer = setTimeout(() => {
        this.#timers.delete(timer);
        fn();
      }, ms);
      this.#timers.add(timer);
    };
    later(() => {
      tile.classList.add(`is-${mode}`);
      later(() => {
        tile.classList.remove(`is-${mode}`);
        delete tile.dataset.busy;
      }, mode === 'enter' ? 1200 : 800);
    }, delay);
  }
}

if (!customElements.get('bl-kit-tiles')) {
  customElements.define('bl-kit-tiles', BlKitTiles);
}

export { BlKitTiles };
